import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Image as ImageIcon, UploadSimple, Trash, Eye, EyeSlash, X, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [afterPreview, setAfterPreview] = useState("");
  const [beforePreview, setBeforePreview] = useState("");
  const [caption, setCaption] = useState("");
  const [service, setService] = useState("");
  const [uploading, setUploading] = useState(false);
  const afterRef = useRef(null);
  const beforeRef = useRef(null);

  const load = () => api.get("/gallery").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const readToDataUrl = (file, setter) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large — keep it under 8 MB"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setter(String(e.target?.result || ""));
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!afterPreview) return toast.error("Choose an 'After' image first");
    setUploading(true);
    try {
      await api.post("/gallery", {
        image_url: afterPreview,
        before_image_url: beforePreview || "",
        caption,
        service,
        is_published: true,
      });
      toast.success(beforePreview ? "Before/after pair added" : "Added to gallery");
      setAfterPreview(""); setBeforePreview(""); setCaption(""); setService("");
      if (afterRef.current) afterRef.current.value = "";
      if (beforeRef.current) beforeRef.current.value = "";
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally { setUploading(false); }
  };

  const togglePublish = async (it) => {
    await api.patch(`/gallery/${it.id}`, { is_published: !it.is_published });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image from the gallery?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Deleted");
    load();
  };

  const removeBeforeOnItem = async (it) => {
    if (!window.confirm("Convert this back to a single 'after' image?")) return;
    await api.patch(`/gallery/${it.id}`, { before_image_url: "" });
    toast.success("Before image removed");
    load();
  };

  return (
    <div data-testid="gallery-page">
      <PageHeader
        eyebrow="Showcase"
        title="Gallery"
        description="Upload photos of completed jobs. Add an optional 'Before' image to create a draggable Before/After comparison on the public site."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="sharp-card p-6 bg-white">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">After (required)</Label>
              <div className="mt-2">
                <input
                  ref={afterRef}
                  data-testid="gallery-file-after"
                  type="file"
                  accept="image/*"
                  onChange={(e) => readToDataUrl(e.target.files?.[0], setAfterPreview)}
                  className="block w-full text-sm file:mr-3 file:px-4 file:py-2 file:rounded-sm file:border-0 file:bg-navy file:text-white file:font-semibold hover:file:bg-navy-deep cursor-pointer"
                />
              </div>
              {afterPreview && (
                <div className="mt-3 relative border border-slate-200 rounded-sm overflow-hidden">
                  <img src={afterPreview} alt="After preview" className="w-full max-h-56 object-cover" />
                  <button onClick={() => { setAfterPreview(""); if (afterRef.current) afterRef.current.value = ""; }} className="absolute top-2 right-2 p-1 bg-white/90 hover:bg-white text-navy rounded-sm" aria-label="Clear after image"><X size={14}/></button>
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-slate-100">
              <Label className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
                <ArrowsLeftRight size={14}/> Before (optional)
              </Label>
              <p className="text-[11px] text-slate-500 mt-1">Add this to publish a draggable Before/After slider on the public site.</p>
              <div className="mt-2">
                <input
                  ref={beforeRef}
                  data-testid="gallery-file-before"
                  type="file"
                  accept="image/*"
                  onChange={(e) => readToDataUrl(e.target.files?.[0], setBeforePreview)}
                  className="block w-full text-sm file:mr-3 file:px-4 file:py-2 file:rounded-sm file:border-0 file:bg-slate-200 file:text-navy file:font-semibold hover:file:bg-slate-300 cursor-pointer"
                />
              </div>
              {beforePreview && (
                <div className="mt-3 relative border border-slate-200 rounded-sm overflow-hidden">
                  <img src={beforePreview} alt="Before preview" className="w-full max-h-56 object-cover" />
                  <button onClick={() => { setBeforePreview(""); if (beforeRef.current) beforeRef.current.value = ""; }} className="absolute top-2 right-2 p-1 bg-white/90 hover:bg-white text-navy rounded-sm" aria-label="Clear before image"><X size={14}/></button>
                </div>
              )}
            </div>

            {afterPreview && beforePreview && (
              <div className="pt-2 border-t border-slate-100">
                <Label className="text-xs uppercase tracking-wider font-semibold mb-2 block">Live preview</Label>
                <BeforeAfterSlider beforeSrc={beforePreview} afterSrc={afterPreview} />
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Caption (optional)</Label>
              <Input data-testid="gallery-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Bathroom tap replacement, Wodonga" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Service (optional)</Label>
              <Input data-testid="gallery-service" value={service} onChange={(e) => setService(e.target.value)} placeholder="e.g. Plumbing" className="rounded-sm mt-1" />
            </div>
            <Button
              data-testid="btn-upload-gallery"
              onClick={upload}
              disabled={!afterPreview || uploading}
              className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11"
            >
              <UploadSimple className="mr-2" size={18} />
              {uploading ? "Uploading..." : beforePreview ? "Add before/after pair" : "Add to gallery"}
            </Button>
          </div>
        </Card>

        <Card className="sharp-card p-6 lg:col-span-2 min-h-[400px]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow eyebrow-dark">Live gallery</div>
              <div className="text-sm text-slate-600 mt-1">{items.length} image{items.length === 1 ? "" : "s"}</div>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="text-center text-slate-500 py-16">
              <ImageIcon size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm">No images yet — upload your first completed job photo.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((it) => {
                const isPair = !!it.before_image_url;
                return (
                  <div key={it.id} data-testid={`gal-${it.id}`} className={`group relative border rounded-sm overflow-hidden bg-slate-50 ${it.is_published ? "border-slate-200" : "border-dashed border-slate-300 opacity-80"}`}>
                    {isPair ? (
                      <BeforeAfterSlider beforeSrc={it.before_image_url} afterSrc={it.image_url} aspect="aspect-[4/3]" />
                    ) : (
                      <div className="aspect-[4/3] bg-slate-100">
                        <img src={it.image_url} alt={it.caption || it.service || "gallery"} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-3 flex items-center justify-between gap-2 border-t border-slate-100">
                      <div className="min-w-0">
                        {it.caption && <div className="text-sm font-medium text-navy truncate">{it.caption}</div>}
                        <div className="text-[11px] text-slate-500 truncate">{it.service || (isPair ? "Before / After" : "Photo")}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {isPair && (
                          <button data-testid={`gal-rm-before-${it.id}`} onClick={() => removeBeforeOnItem(it)} title="Remove Before image" className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-navy rounded-sm"><ArrowsLeftRight size={14}/></button>
                        )}
                        <button data-testid={`gal-toggle-${it.id}`} onClick={() => togglePublish(it)} title={it.is_published ? "Hide" : "Publish"} className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-navy rounded-sm">
                          {it.is_published ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                        <button data-testid={`gal-del-${it.id}`} onClick={() => remove(it.id)} title="Delete" className="p-1.5 hover:bg-red-50 text-slate-500 hover:text-red-500 rounded-sm">
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                    {!it.is_published && (
                      <div className="absolute top-2 left-2 bg-slate-900/80 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm">Hidden</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
